import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Circle, Upload, Settings, BarChart2, ChevronUp, ChevronDown, Info, X} from 'lucide-react';
import server from '../../../api/server';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext.jsx';

const ProjectStatusComponent = ({serviceID, clientID, dontShow, hitStatusApi}) => {
  const { showToast } = useToast();
  const [isSetupExpanded, setIsSetupExpanded] = useState(true);
    const [steps, setSteps] = useState([
      { id: 'create-project', label: 'Create Project', completed: false, current: false, icon: <CheckCircle className="h-5 w-5" /> },
      { id: 'upload-records', label: 'Upload Records', completed: false, current: false, icon: <Upload className="h-5 w-5" /> },
      { id: 'config-apps', label: 'Config Apps', completed: false, current: false, icon: <Settings className="h-5 w-5" /> },
      { id: 'run-analysis', label: 'Completed', completed: false, current: false, icon: <BarChart2 className="h-5 w-5" /> }
    ]);
    const [client_id, setClient_id] = useState(null);
    const [email, setEmail] = useState(null);
    const navigate = useNavigate()
    const [showDone, setShowDone] = useState(false)
    const [serverStatus, setServerStatus] = useState(null);
    const lastPostedRef = useRef(null);
    
    const completedSteps = steps.filter(step => step.completed).length;
    const totalSteps = steps.length;
    const progress = (completedSteps / totalSteps) * 100;
    function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      const match = document.cookie.match(new RegExp('(^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[2] : null;
    }

    useEffect(()=> {
      const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
      setEmail(user?.email)
      if(clientID){
        setClient_id(clientID);
      }
    }, [clientID])

    useEffect(() => {
      if(client_id && serviceID && email){
        getProjectStatus();
      }
    }, [client_id, serviceID, email]);

    useEffect(() => {
      if(!hitStatusApi){
        return;
      }
      // Prevent re-posting the exact same status (e.g., on remount)
      if(lastPostedRef.current === hitStatusApi){
        return;
      }
      // Never downgrade a completed project back to uploaded
      if(serverStatus === 'completed' && hitStatusApi === 'uploaded'){
        return;
      }
      lastPostedRef.current = hitStatusApi;
      updateProjectStatus(hitStatusApi);
    }, [hitStatusApi, serverStatus]);
    const handleDontShow = () => {
      dontShow(true)
    }

    const getProjectStatus = async () => {
      try {
        const resp = await server.getProjectStatus(client_id, serviceID, email, 'home', undefined, window.location.href);
        if(resp.status === 200){
          const data = await resp.json();
          setServerStatus(data.data);
          if(data.data === 'start' || data.data === null || data.data === undefined){
            // Initial state - only create project is completed
            setSteps(prevSteps => prevSteps.map(step => ({
              ...step,
              completed: step.id === 'create-project',
              current: step.id === 'upload-records'
            })));
          } else if(data.data === 'uploaded') {
            // After upload - create project, upload, and config apps are completed
            setSteps(prevSteps => prevSteps.map(step => ({
              ...step,
              completed: step.id === 'create-project' || step.id === 'upload-records',
              current: step.id === 'config-apps'
            })));
          } else if(data.data === 'run-analysis') {
            // After analysis - all steps are completed
            setSteps(prevSteps => prevSteps.map(step => ({
              ...step,
              completed: step.id === 'create-project' || step.id === 'upload-records' || step.id === 'config-apps',
              current: step.id === 'run-analysis'
            })));
          } else {
            // Handle any other response
            setSteps(prevSteps => prevSteps.map(step => ({
              ...step,
              completed: true,
              current: false
            })));
          }
        }
      } catch (error) {
      }
    }

    const gotoStep = (stepId) => {
      if(stepId === 'create-project'){
        navigate(`/analytics/${client_id}/${serviceID}/create-project`);
      } else if(stepId === 'upload-records'){
        navigate(`/analytics/${client_id}/${serviceID}/data-sources`);
      } else if(stepId === 'config-apps'){
        if(showDone === true){
          updateProjectStatus('completed')
          handleApply()
          getProjectStatus()
          return;
        }
        setShowDone(true)
        navigate(`/analytics/${client_id}/${serviceID}/apps`);
      }else if(stepId === 'run-analysis'){
        updateProjectStatus('completed')
        getProjectStatus()
      }
    }
    const updateProjectStatus = async(step) => {
      try {
        let res = await server.postProjectStatus({client_id, service_id: Array.isArray(serviceID) ? serviceID[0] : serviceID, email, menu: 'apps', submenu: undefined, 'completion_status': step})
        if(res.status === 200){
          getProjectStatus()
        }
        setShowDone(false)
      } catch (error) {
      }
    }
    const handleApply = async () => {
      try {
        let res = await server.applyConversationChanges({
          client_id,
          service_id: Array.isArray(serviceID) ? serviceID[0] : serviceID,
          email,
          menu: 'apps',
          submenu: undefined,
          configuration: undefined
        })
        if(res.status === 200) {
          showToast('Project Setup Completed. We will notify once uploaded calls are processed.', "success")
        }
      } catch (error) {
      }
    }
        
    return (
      <div className="w-full bg-white border-b border-gray-200 shadow-sm">
        <div className="mx-auto">
          <div className="px-4 py-3">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="bg-gray-100 rounded-full h-8 w-8 flex items-center justify-center mr-3">
                  <Info className="h-5 w-5 text-black" />
                </div>
                <span className="font-medium text-lg" style={{fontSize: "1rem"}}>Setup Project</span>
                <span className="ml-2 bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-sm">
                  {completedSteps}/{totalSteps} Completed
                </span>
              </div>
              <div className="flex items-center space-x-3">
                {completedSteps===4 && <button onClick={handleDontShow}><X style={{color: "slategray", height: "16px"}}/></button>}
                <button 
                  onClick={() => setIsSetupExpanded(!isSetupExpanded)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {isSetupExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </div>
            </div>
            
            {isSetupExpanded && (
              <div className="mt-2 relative">
                <div className="absolute h-1 bg-gray-200 top-6 z-0" style={{ left: '11%', width: '78%' }}></div>
        
                {/* Active Progress Bar */}
                <div
                  className="absolute h-1 bg-black top-6 z-0"
                  style={{ left: '11%', width: `${(completedSteps / (totalSteps - 1)) * 58}%` }}
                ></div>


                
                {/* Steps */}
                <div className="flex justify-between mb-2 z-10">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex flex-col items-center relative" style={{ width: '24%' }}>
                      {/* Connector line */}
                      {index < steps.length - 1 && (
                        <div className={`absolute top-6 left-[100%] w-full h-0.5 ${index < completedSteps ? 'bg-black' : 'bg-gray-200'} -z-10`}></div>
                      )}
                      
                      {/* Step indicator */}
                      <div className={`flex items-center justify-center h-10 w-10 rounded-full mb-3 ${
                        step.completed 
                          ? 'bg-black text-white' 
                          : step.current 
                            ? 'bg-black text-white' 
                            : 'bg-gray-100 text-gray-400'
                      }`}>
                        {step.completed ? (
                          <CheckCircle className="h-6 w-6" />
                        ) : step.current ? (
                          <Upload className="h-6 w-6" />
                        ) : step.id === 'config-apps' ? (
                          <Settings className="h-6 w-6" />
                        ) : (
                          <BarChart2 className="h-6 w-6" />
                        )}
                      </div>
                      
                      {/* Label */}
                      <span className={`text-base font-medium ${
                        step.completed 
                          ? 'text-black' 
                          : step.current 
                            ? 'text-black' 
                            : 'text-gray-500'
                      }`} style={{fontSize: "14px"}}>
                      {step.label}
                      </span>
                      
                      {step.current && (
                        <button onClick={() => gotoStep(step.id)} className="mt-2 px-6 py-2 bg-black hover:bg-gray-800 text-white text-sm rounded-md font-medium">
                          {!showDone ? 'Start Now' : 'Done'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

export default ProjectStatusComponent